/**
 * ReasoningPanel — Reusable LLM reasoning display component.
 *
 * Renders a collapsible panel showing the LLM's chain-of-thought
 * reasoning steps, tool actions, and live streaming text.
 *
 * Props:
 *   reasoningSteps  {Array}    [{seq, text, source, timestamp}]
 *   liveText        {string}   Current reasoning text being streamed
 *   toolActions     {Array}    [{tool, status, args, result, timestamp}]
 *   isStreaming      {boolean}  Whether reasoning is actively streaming
 *   compact         {boolean}  Compact mode for inline use
 *   defaultOpen     {boolean}  Whether panel starts expanded (default: true)
 *   title           {string}   Panel title (default: "LLM Reasoning")
 */
import React, { useState, useEffect, useRef } from 'react';
import './ReasoningPanel.css';

const ToolActionRow = ({ action }) => {
    const isActive = action.status === 'started';
    const isFailed = action.status === 'failed';

    return (
        <div className={`rp-tool-action ${isActive ? 'rp-tool-active' : ''} ${isFailed ? 'rp-tool-failed' : ''}`}>
            <span className="rp-tool-icon">
                {isActive ? '⚙️' : isFailed ? '❌' : '✅'}
            </span>
            <span className="rp-tool-name">{action.tool}</span>
            <span className="rp-tool-status">
                {action.status}
            </span>
            {action.args && action.status === 'started' && (
                <span className="rp-tool-args">
                    {Object.entries(action.args)
                        .slice(0, 2)
                        .map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`)
                        .join(', ')}
                </span>
            )}
        </div>
    );
};

const ReasoningStepRow = ({ step, isLast }) => (
    <div className={`rp-step ${isLast ? 'rp-step-latest' : ''}`}>
        <span className="rp-step-dot" />
        <span className="rp-step-text">{step.text}</span>
    </div>
);

const ReasoningPanel = ({
    reasoningSteps = [],
    liveText = '',
    toolActions = [],
    isStreaming = false,
    compact = false,
    defaultOpen = true,
    title = 'LLM Reasoning',
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const scrollRef = useRef(null);

    const stepCount = reasoningSteps.length;
    const hasContent = stepCount > 0 || liveText || toolActions.length > 0;

    useEffect(() => {
        if (scrollRef.current && isOpen) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [reasoningSteps, liveText, toolActions, isOpen]);

    if (!hasContent && !isStreaming) return null;

    return (
        <div className={`reasoning-panel ${isOpen ? 'rp-open' : 'rp-closed'} ${compact ? 'rp-compact' : ''}`}>
            <div
                className="rp-header"
                onClick={() => setIsOpen(prev => !prev)}
            >
                <span className="rp-toggle">{isOpen ? '▾' : '▸'}</span>
                <span className="rp-title">{title}</span>
                {stepCount > 0 && (
                    <span className="rp-step-count">
                        {stepCount} step{stepCount !== 1 ? 's' : ''}
                    </span>
                )}
                {isStreaming && (
                    <span className="rp-streaming-indicator">
                        <span className="rp-spinner" />
                        Thinking...
                        <span className="rp-latency-hint"> · usually takes up to a minute</span>
                    </span>
                )}
            </div>

            {isOpen && (
                <div className="rp-body" ref={scrollRef}>
                    {reasoningSteps.map((step, i) => (
                        <ReasoningStepRow
                            key={step.seq || i}
                            step={step}
                            isLast={i === stepCount - 1 && !liveText}
                        />
                    ))}

                    {liveText && (
                        <div className="rp-step rp-step-live">
                            <span className="rp-step-dot rp-dot-pulse" />
                            <span className="rp-step-text">{liveText}</span>
                            <span className="rp-cursor" />
                        </div>
                    )}

                    {toolActions.map((action, i) => (
                        <ToolActionRow key={`${action.tool}-${i}`} action={action} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReasoningPanel;
